import React, { useState, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import Header from './components/Header/Header';
import Footer from './components/Footer/Footer';
import Home from './pages/Home/Home';
import About from './pages/About/About';
import Contact from './pages/Contact/Contact';
import AppDetails from './pages/AppDetails/AppDetails';
import { ThemeContext } from './contexts/ThemeContext';
import { ThemeOption } from './types/theme';
import './App.css';

function App() {
  // Define theme options
  const themeOptions: ThemeOption[] = [
    { name: 'Default Blue', primary: '#3f51b5', secondary: '#f50057', background: '#ffffff' },
    { name: 'Dark Mode', primary: '#bb86fc', secondary: '#03dac6', background: '#121212' },
    { name: 'Forest Green', primary: '#2e7d32', secondary: '#ff6d00', background: '#f1f8e9' },
    { name: 'Ocean Blue', primary: '#0288d1', secondary: '#f57c00', background: '#e1f5fe' },
    { name: 'Purple Haze', primary: '#673ab7', secondary: '#ff4081', background: '#f3e5f5' },
    { name: 'Sunset Orange', primary: '#ff5722', secondary: '#2196f3', background: '#fff3e0' },
    { name: 'Berry Wine', primary: '#ad1457', secondary: '#26a69a', background: '#fce4ec' },
    { name: 'Midnight Blue', primary: '#1a237e', secondary: '#ff9800', background: '#e8eaf6' },
    { name: 'Emerald City', primary: '#00695c', secondary: '#d81b60', background: '#e0f2f1' },
    { name: 'Amber Glow', primary: '#ff8f00', secondary: '#5e35b1', background: '#fff8e1' },
    { name: 'Dark Teal', primary: '#00796b', secondary: '#ec407a', background: '#212121' },
    { name: 'Nordic Frost', primary: '#0097a7', secondary: '#ff6e40', background: '#f0f7fa' },
    { name: 'Tropical Vibes', primary: '#00bcd4', secondary: '#ff4081', background: '#e0f7fa' },
    { name: 'Golden Hour', primary: '#ffc107', secondary: '#9c27b0', background: '#fffde7' },
    { name: 'Cherry Blossom', primary: '#e91e63', secondary: '#3f51b5', background: '#fce4ec' },
    { name: 'Tech Slate', primary: '#546e7a', secondary: '#26a69a', background: '#eceff1' },
    { name: 'Futuristic', primary: '#512da8', secondary: '#00bcd4', background: '#303030' },
  ];

  // State for the currently selected theme
  const [themeName, setThemeName] = useState('Ocean Blue');

  // Get the selected theme object
  const selectedTheme = themeOptions.find((theme) => theme.name === themeName) || themeOptions[0];

  // Create a theme instance
  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: themeName === 'Dark Mode' || themeName === 'Dark Teal' || themeName === 'Futuristic' ? 'dark' : 'light',
          primary: {
            main: selectedTheme.primary,
          },
          secondary: {
            main: selectedTheme.secondary,
          },
          background: {
            default: selectedTheme.background,
          },
        },
        typography: {
          fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          h1: {
            fontWeight: 700,
          },
          h2: {
            fontWeight: 600,
          },
        },
      }),
    [themeName, selectedTheme]
  );

  // Get the basename for GitHub or Bitbucket Pages
  const getBasename = () => {
    // Extract repository name from package.json homepage or use empty string for local development
    const { homepage } = require('../package.json');
    if (!homepage || homepage === '.') return '';

    // Parse the hostname portion from the homepage URL
    try {
      const url = new URL(homepage);
      const pathSegments = url.pathname.split('/').filter(Boolean);
      return pathSegments.length > 0 ? `/${pathSegments[0]}` : '';
    } catch (e) {
      // If homepage is not a valid URL or is a relative path
      if (homepage.startsWith('/')) return homepage;
      if (homepage === '.') return '';

      // Check if we're running on Bitbucket Pages
      // Bitbucket Pages URLs are typically: https://<username>.bitbucket.io/<repo-name>/
      const bitbucketMatch = window.location.hostname.match(/([^.]+)\.bitbucket\.io/i);
      if (bitbucketMatch) {
        // Get the repository name from the pathname
        const pathSegments = window.location.pathname.split('/').filter(Boolean);
        return pathSegments.length > 0 ? `/${pathSegments[0]}` : '';
      }

      return `/${homepage}`;
    }
  };

  return (
    <ThemeContext.Provider value={{ themeName, setThemeName, themeOptions }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router basename={getBasename()}>
          <Header />
          <main className="main-content">
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/app/:appId" element={<AppDetails />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
            </Routes>
          </main>
          <Footer />
        </Router>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
